import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SEO from "../components/seo/SEO";
import ApiServices from "../../Utils/ApiServices/ApiServices";
import CryptoJS from "crypto-js";

/* ================= OTP INPUT ================= */
const OtpInput = ({ otp, setOtp }) => {
  const refs = useRef([]);

  const handleChange = (i, v) => {
    if (!/^[0-9]?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(paste)) return;
    setOtp(paste.split(""));
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }} onPaste={handlePaste}>
      {[...Array(6)].map((_, i) => (
        <TextField
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={otp[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
          sx={{ width: 48 }}
        />
      ))}
    </Box>
  );
};

/* ================= FORGOT PASSWORD ================= */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;

  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [loading, setLoading] = useState(false);

  const [otpDialog, setOtpDialog] = useState(false);
  const [otp, setOtp] = useState([]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  /* ================= EMAIL SUBMIT ================= */
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!isEmailValid) return;

    setLoading(true);
    try {
      const res = await ApiServices.post(
        "/apis/default/api/emailforgotpassword",
        { username: email, email }
      );

      if (res.data.data.status) {
        setOtpDialog(true);
        setMessage("OTP sent successfully");
      } else {
        setError("Failed to send OTP");
      }
    } catch {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESET PASSWORD ================= */
  const handleResetPassword = async () => {
    try {
      const res = await ApiServices.post(
        "/apis/default/api/patientpassreset",
        {
          login_uname: email,
          forgot_otp: otp.join(""),
          pass_new: password,
          pass_new_confirm: confirmPassword,
        }
      );

      if (!res.data.data.status) throw new Error();

      setMessage("Password reset successful");

      /* AUTO LOGIN */
    //   const { data } = await ApiServices.post(
    //     "/oauth2/default/token",
    //     {
    //       user_role: "patient",
    //       username: email,
    //       email,
    //       password,
    //       client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
    //       scope: process.env.REACT_APP_PATIENT_SCOPES,
    //       grant_type: "password",
    //     },
    //     { "Content-Type": "application/x-www-form-urlencoded" }
    //   );

    //   sessionStorage.setItem(
    //     "LoginUserAccessToken",
    //     CryptoJS.AES.encrypt(data.access_token, secretKey).toString()
    //   );
    //   sessionStorage.setItem(
    //     "LoginUserRefreshToken",
    //     CryptoJS.AES.encrypt(data.refresh_token, secretKey).toString()
    //   );
    //   sessionStorage.setItem(
    //     "LoginUserType",
    //     CryptoJS.AES.encrypt("Patient", secretKey).toString()
    //   );

      setTimeout(() => navigate("/signin"), 1500);
    } catch {
      setError("OTP verification failed");
    }
  };

  return (
    <>
      <SEO title="Forgot Password" description="Reset your Rite Wellness password" canonical="/forgot-password" noIndex />

      <Box className="wellness-bg" sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: "100%", maxWidth: 420 }}>
          <Box className="glass-card" sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box component={Link} to="/" sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none" }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FavoriteRoundedIcon sx={{ color: "#fff" }} />
                </Box>
                <Typography fontWeight={800}>Rite <span style={{ color: "#27A599" }}>Wellness</span></Typography>
              </Box>
              <Typography sx={{ mt: 2, fontWeight: 800, fontSize: 22 }}>Reset Password</Typography>
            </Box>

            <Box component="form" onSubmit={handleSendOtp} sx={{ display: "grid", gap: 2 }}>
              <TextField
                placeholder="Email Address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value));
                }}
                error={!isEmailValid}
                helperText={!isEmailValid && "Enter valid email"}
              />

              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={22} /> : "Send OTP"}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: "center", fontSize: 14 }}>
              <Link to="/signin">Patient Login</Link><br />
              <Link to="/provider-signin">Doctor Login</Link>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* OTP DIALOG */}
      <Dialog open={otpDialog}>
        <DialogTitle>Set New Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            type={showPassword ? "text" : "password"}
            placeholder="New Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordsMatch(e.target.value === confirmPassword);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            type={showPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setPasswordsMatch(password === e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {passwordsMatch ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                </InputAdornment>
              )
            }}
            helperText={!passwordsMatch && "Passwords do not match"}
            error={!passwordsMatch}
          />

          <OtpInput otp={otp} setOtp={setOtp} />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOtpDialog(false)}>Cancel</Button>
          <Button disabled={!passwordsMatch} onClick={handleResetPassword}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error || !!message} autoHideDuration={4000} onClose={() => { setError(null); setMessage(null); }}>
        <MuiAlert severity={error ? "error" : "success"} variant="filled">
          {error || message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
